'use client';

import { useLogger } from 'next-axiom';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState, type ComponentProps } from 'react';
import type { z } from 'zod/v4';

import { emailOtp } from '@edgevanta/auth/client';
import Button from '@edgevanta/ui/components/button';
import Form from '@edgevanta/ui/components/form';
import { Loader2 } from '@edgevanta/ui/components/icon';
import {
  useForm,
  zodResolver,
  type SubmitHandler,
} from '@edgevanta/ui/lib/react-hook-form';
import toast from '@edgevanta/ui/lib/toast';
import { cn } from '@edgevanta/ui/lib/utils';

import { FormEmail } from '@/components/form/form-email';
import HookFormDevtool from '@/components/hookform-devtool';
import TurnstileWidget, {
  type TurnstileRef,
} from '@/components/turnstile-widget';
import { redactPII } from '@/lib/axiom/obfuscate';
import env from '@/lib/env/client';

import { signInWithEmailSchema } from '../schema';

type FormValues = z.infer<typeof signInWithEmailSchema>;

type SignInWithEmailFormProps = ComponentProps<'form'> & {
  redirectTo?: string;
  email?: string;
};

export default function SignInWithEmailForm({
  redirectTo,
  email,
  className,
  ...props
}: SignInWithEmailFormProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileRef>(null);

  const { push } = useRouter();

  const log = useLogger().with({ url: '/sign-in' });

  const { formState, control, handleSubmit, setValue, ...form } =
    useForm<FormValues>({
      mode: 'onChange',
      defaultValues: {
        email: '',
      },
      resolver: zodResolver(signInWithEmailSchema),
    });

  useEffect(() => {
    if (email) {
      setValue('email', email, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  }, [email, setValue]);

  const onSubmit: SubmitHandler<FormValues> = async (values: FormValues) => {
    try {
      setIsLoading(true);

      const headers: Record<string, string> = {};

      if (env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && turnstileToken) {
        headers['x-captcha-response'] = turnstileToken;
      }

      const { data, error } = await emailOtp.sendVerificationOtp({
        email: values.email,
        type: 'sign-in',
        fetchOptions: {
          headers,
        },
      });

      if (data?.success) {
        push(
          `/sign-in/otp?email=${encodeURIComponent(values.email)}${redirectTo ? `&redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
        );
      }

      if (error) {
        log.info('Sign-in error', redactPII({ email: values.email, error }));

        if (env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
          turnstileRef.current?.reset();

          setTurnstileToken(null);
        }

        const message = error.message || error.statusText;
        const isCaptchaError =
          message?.toLowerCase().includes('captcha') || error.status === 403;

        Sentry.captureException(
          new Error(
            isCaptchaError
              ? 'Sign-in blocked: captcha failure'
              : 'Sign-in blocked',
          ),
          {
            level: isCaptchaError ? 'warning' : 'error',
            fingerprint: isCaptchaError
              ? ['auth', 'sign-in-blocked', 'captcha']
              : ['auth', 'sign-in-blocked'],
            extra: {
              ...error,
              originalMessage: message,
              email: redactPII({ email: values.email }).email,
            },
            tags: {
              source: 'emailOtp.sendVerificationOtp',
              'sign-in-blocked': 'true',
              ...(isCaptchaError ? { 'captcha-failure': 'true' } : {}),
            },
          },
        );

        if (isCaptchaError) {
          toast.error(
            'Security verification was blocked. Disable ad blockers or privacy extensions for this page, refresh, and try again. If it persists, try a different browser or an incognito window with extensions turned off.',
            { duration: 10000 },
          );
        } else if (error.status < 500) {
          toast.warning(message);
        } else {
          toast.error(`Sign-in error: ${message}`);
        }
      } else {
        if (env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
          setTurnstileToken(null);
        }
      }
    } catch (err) {
      log.error(
        'Sign-In unexpected error',
        redactPII({ email: values.email, error: (err as Error).message }),
      );

      if (env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
        turnstileRef.current?.reset();

        setTurnstileToken(null);
      }

      toast.error((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Form {...{ formState, control, handleSubmit, setValue, ...form }}>
        <form
          className={cn('space-y-4', className)}
          onSubmit={handleSubmit(onSubmit)}
          {...props}
        >
          <div className="space-y-2">
            <FormEmail
              className="[&_label]:sr-only [&_input]:h-12"
              control={control}
              label="Email"
              name="email"
              placeholder="Enter your email"
            />
          </div>

          <TurnstileWidget ref={turnstileRef} setToken={setTurnstileToken} />

          <Button
            className="w-full cursor-pointer"
            disabled={
              !formState.isValid ||
              !formState.isDirty ||
              isLoading ||
              (!!env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && !turnstileToken)
            }
            size="lg"
            type="submit"
          >
            {isLoading ? <Loader2 className="animate-spin" /> : 'Continue'}
          </Button>
        </form>
      </Form>
      {formState.isDirty ? <HookFormDevtool control={control} /> : null}
    </>
  );
}
